import { useToastController } from '@tamagui/toast';
import { useState } from 'react';
import { isWeb, View, XStack, YStack } from 'tamagui';

import {
    Button,
    ButtonSize,
    ButtonVariant,
} from '../../components/buttons/Button';
import { DateRangeSelectionSheet } from '../../components/date/DateRangeSelectionSheet';
import { DateSelectionSheet } from '../../components/date/DateSelectionSheet';
import { Picker } from '../../components/inputs/Picker';
import { IconSelectDropdown } from '../../components/select/IconSelectDropdown';
import { SelectDropdown } from '../../components/select/SelectDropdown';
import { Sheet } from '../../components/sheet/Sheet';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
} from '../../components/text/Typography';
import {
    InfoButtonWithTooltip,
    TooltipGroupProvider,
} from '../../components/tooltip/Tooltip';

const ROOT_TOOLTIP_GROUP = 'blender-sheet-tooltip-toast-root';
const STANDARD_SHEET_TOOLTIP_GROUP = 'blender-sheet-tooltip-toast-standard';
const KEYBOARD_SHEET_TOOLTIP_GROUP = 'blender-sheet-tooltip-toast-keyboard';

const SELECT_DROPDOWN_OPTIONS = ['Monthly', 'Quarterly', 'Yearly'];
const PICKER_OPTIONS = [
    { label: 'Monthly', value: 'monthly' },
    { label: 'Quarterly', value: 'quarterly' },
    { label: 'Yearly', value: 'yearly' },
];
const ICON_SELECT_DROPDOWN_OPTIONS = [
    { value: 'home', icon: 'home', label: 'Home', color: 'blue' },
    {
        value: 'piggyBank',
        icon: 'piggyBank',
        label: 'Piggy Bank',
        color: 'red',
    },
    {
        value: 'briefCase',
        icon: 'briefCase',
        label: 'Brief Case',
        color: 'green',
    },
];

/** Resolves the toast controller from inside the sheet, rather than from the component rendering the sheet */
const ToastFromInsideSheetButton = () => {
    const toast = useToastController();

    return (
        <View marginTop={24}>
            <Button
                variant={ButtonVariant.SECONDARY}
                size={ButtonSize.SMALL}
                text="Show Toast (controller from inside sheet)"
                onPress={() => {
                    toast.show('Toast triggered from inside the sheet!');
                }}
            />
        </View>
    );
};

export const PortalTestingGallery = () => {
    const [isStandardSheetOpen, setIsStandardSheetOpen] = useState(false);
    const [isKeyboardSheetOpen, setIsKeyboardSheetOpen] = useState(false);

    const [
        isNestedFromStandardStandardOpen,
        setIsNestedFromStandardStandardOpen,
    ] = useState(false);
    const [
        isNestedFromStandardKeyboardOpen,
        setIsNestedFromStandardKeyboardOpen,
    ] = useState(false);
    const [
        isNestedFromKeyboardStandardOpen,
        setIsNestedFromKeyboardStandardOpen,
    ] = useState(false);
    const [
        isNestedFromKeyboardKeyboardOpen,
        setIsNestedFromKeyboardKeyboardOpen,
    ] = useState(false);

    const toast = useToastController();

    const SheetDropdownExamples = () => {
        const [selectValue, setSelectValue] = useState('Monthly');
        const [iconSelectValue, setIconSelectValue] = useState<string>('home');
        const [pickerValue, setPickerValue] = useState<string | undefined>();

        return (
            <YStack
                gap={12}
                marginVertical={32}
                alignItems="center"
                width={320}
                alignSelf="center"
            >
                <BodySmallRegular>Dropdown selectors</BodySmallRegular>
                {isWeb && (
                    <Picker
                        size="tiny"
                        value={pickerValue}
                        options={PICKER_OPTIONS}
                        placeholder="Select a frequency"
                        onChange={setPickerValue}
                        onPressDone={setPickerValue}
                    />
                )}
                <SelectDropdown
                    defaultValue={selectValue}
                    options={SELECT_DROPDOWN_OPTIONS}
                    onSelect={(selectedItem) => {
                        setSelectValue(selectedItem);
                    }}
                />
                <IconSelectDropdown
                    options={ICON_SELECT_DROPDOWN_OPTIONS}
                    value={iconSelectValue}
                    onValueChange={(value) => {
                        setIconSelectValue(value);
                    }}
                />
            </YStack>
        );
    };

    const SheetDateSelectionExamples = () => {
        const [singleDate, setSingleDate] = useState(new Date());
        const [isSingleDateSheetOpen, setIsSingleDateSheetOpen] =
            useState(false);
        const [fromDate, setFromDate] = useState(new Date('2025-01-01'));
        const [toDate, setToDate] = useState(new Date());
        const [isDateRangeSheetOpen, setIsDateRangeSheetOpen] = useState(false);

        return (
            <YStack gap={12} alignItems="center" width={320} alignSelf="center">
                <Button
                    variant={ButtonVariant.SECONDARY}
                    size={ButtonSize.SMALL}
                    text="Open DateSelectionSheet"
                    onPress={() => {
                        setIsSingleDateSheetOpen(true);
                    }}
                />
                <Button
                    variant={ButtonVariant.SECONDARY}
                    size={ButtonSize.SMALL}
                    text="Open DateRangeSelectionSheet"
                    onPress={() => {
                        setIsDateRangeSheetOpen(true);
                    }}
                />

                <DateSelectionSheet
                    title="Select date"
                    isOpen={isSingleDateSheetOpen}
                    onClose={() => {
                        setIsSingleDateSheetOpen(false);
                    }}
                    defaultValue={singleDate}
                    onConfirm={setSingleDate}
                    confirmButtonText="Set date"
                />
                <DateRangeSelectionSheet
                    isOpen={isDateRangeSheetOpen}
                    onClose={() => {
                        setIsDateRangeSheetOpen(false);
                    }}
                    defaultFromDate={fromDate}
                    defaultToDate={toDate}
                    onConfirm={(nextFromDate, nextToDate) => {
                        setFromDate(nextFromDate);
                        setToDate(nextToDate);
                    }}
                    confirmButtonText="Set range"
                />
            </YStack>
        );
    };

    const ActionButtonWithTooltip = ({
        buttonText,
        tooltipText,
        tooltipGroupName,
        onPress,
    }: {
        buttonText: string;
        tooltipText: string;
        tooltipGroupName: string;
        onPress: () => void;
    }) => {
        return (
            <XStack gap={8} alignItems="center" marginTop={24}>
                <View flex={1} paddingLeft={40}>
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.SMALL}
                        text={buttonText}
                        onPress={onPress}
                    />
                </View>
                <InfoButtonWithTooltip
                    tooltipContent={tooltipText}
                    tooltipGroupName={tooltipGroupName}
                    subtle
                />
            </XStack>
        );
    };

    const NestedToastSheet = ({
        isOpen,
        onClose,
        title,
        forceKeyboardCompatibleBottomSheet,
    }: {
        isOpen: boolean;
        onClose: () => void;
        title: string;
        forceKeyboardCompatibleBottomSheet?: boolean;
    }) => {
        const toast = useToastController();
        return (
            <Sheet
                isOpen={isOpen}
                onClose={onClose}
                title={title}
                showCloseButton
                forceKeyboardCompatibleBottomSheet={
                    forceKeyboardCompatibleBottomSheet
                }
            >
                <View marginTop={24}>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.SMALL}
                        text="Show Toast"
                        onPress={() => {
                            toast.show(
                                'Toast triggered from nested sheet button!',
                                {
                                    viewportName: 'nested-toast-sheet',
                                },
                            );
                        }}
                    />
                </View>
                <SheetDropdownExamples />
                <SheetDateSelectionExamples />
            </Sheet>
        );
    };

    return (
        <YStack gap={16} width="100%" maxWidth={600}>
            <BodyMediumRegular>
                This page demonstrates complex layering and interactions of
                components that use portals, or portal-like layering:
            </BodyMediumRegular>
            <BodyMediumEmphasized>
                Sheets, Toasts, Tooltips, dropdown selectors, and date selectors
            </BodyMediumEmphasized>
            <ActionButtonWithTooltip
                buttonText="Open Standard Sheet"
                tooltipText="Opens a regular sheet."
                tooltipGroupName={ROOT_TOOLTIP_GROUP}
                onPress={() => {
                    setIsStandardSheetOpen(true);
                }}
            />
            <ActionButtonWithTooltip
                buttonText="Open Keyboard-Compatible Sheet"
                tooltipText="Opens a sheet with forceKeyboardCompatibleBottomSheet."
                tooltipGroupName={ROOT_TOOLTIP_GROUP}
                onPress={() => {
                    setIsKeyboardSheetOpen(true);
                }}
            />
            <TooltipGroupProvider tooltipGroupName={ROOT_TOOLTIP_GROUP} />

            <Sheet
                isOpen={isStandardSheetOpen}
                onClose={() => {
                    setIsStandardSheetOpen(false);
                }}
                title="Standard Sheet"
                showCloseButton
                forceKeyboardCompatibleBottomSheet={false}
            >
                <ActionButtonWithTooltip
                    buttonText="Open Standard Sheet"
                    tooltipText="Opens a nested standard sheet."
                    tooltipGroupName={STANDARD_SHEET_TOOLTIP_GROUP}
                    onPress={() => {
                        setIsNestedFromStandardStandardOpen(true);
                    }}
                />
                <ActionButtonWithTooltip
                    buttonText="Open Keyboard-Compatible Sheet"
                    tooltipText="Opens a nested keyboard-compatible sheet."
                    tooltipGroupName={STANDARD_SHEET_TOOLTIP_GROUP}
                    onPress={() => {
                        setIsNestedFromStandardKeyboardOpen(true);
                    }}
                />
                <ActionButtonWithTooltip
                    buttonText="Show Toast"
                    tooltipText="Shows a toast from this standard sheet."
                    tooltipGroupName={STANDARD_SHEET_TOOLTIP_GROUP}
                    onPress={() => {
                        toast.show('Toast triggered from standard sheet!');
                    }}
                />
                <ToastFromInsideSheetButton />
                <SheetDropdownExamples />
                <SheetDateSelectionExamples />
                <TooltipGroupProvider
                    tooltipGroupName={STANDARD_SHEET_TOOLTIP_GROUP}
                />
            </Sheet>

            <Sheet
                isOpen={isKeyboardSheetOpen}
                onClose={() => {
                    setIsKeyboardSheetOpen(false);
                }}
                title="Keyboard-Compatible Sheet"
                showCloseButton
                forceKeyboardCompatibleBottomSheet
            >
                <ActionButtonWithTooltip
                    buttonText="Open Standard Sheet"
                    tooltipText="Opens a nested standard sheet."
                    tooltipGroupName={KEYBOARD_SHEET_TOOLTIP_GROUP}
                    onPress={() => {
                        setIsNestedFromKeyboardStandardOpen(true);
                    }}
                />
                <ActionButtonWithTooltip
                    buttonText="Open Keyboard-Compatible Sheet"
                    tooltipText="Opens a nested keyboard-compatible sheet."
                    tooltipGroupName={KEYBOARD_SHEET_TOOLTIP_GROUP}
                    onPress={() => {
                        setIsNestedFromKeyboardKeyboardOpen(true);
                    }}
                />
                <ActionButtonWithTooltip
                    buttonText="Show Toast"
                    tooltipText="Shows a toast from this keyboard-compatible sheet."
                    tooltipGroupName={KEYBOARD_SHEET_TOOLTIP_GROUP}
                    onPress={() => {
                        toast.show(
                            'Toast triggered from keyboard-compatible sheet!',
                        );
                    }}
                />
                <ToastFromInsideSheetButton />
                <SheetDropdownExamples />
                <SheetDateSelectionExamples />
                <TooltipGroupProvider
                    tooltipGroupName={KEYBOARD_SHEET_TOOLTIP_GROUP}
                />
            </Sheet>

            <NestedToastSheet
                isOpen={isNestedFromStandardStandardOpen}
                onClose={() => {
                    setIsNestedFromStandardStandardOpen(false);
                }}
                title="Nested Standard Sheet"
            />
            <NestedToastSheet
                isOpen={isNestedFromStandardKeyboardOpen}
                onClose={() => {
                    setIsNestedFromStandardKeyboardOpen(false);
                }}
                title="Nested Keyboard-Compatible Sheet"
                forceKeyboardCompatibleBottomSheet
            />
            <NestedToastSheet
                isOpen={isNestedFromKeyboardStandardOpen}
                onClose={() => {
                    setIsNestedFromKeyboardStandardOpen(false);
                }}
                title="Nested Standard Sheet"
            />
            <NestedToastSheet
                isOpen={isNestedFromKeyboardKeyboardOpen}
                onClose={() => {
                    setIsNestedFromKeyboardKeyboardOpen(false);
                }}
                title="Nested Keyboard-Compatible Sheet"
                forceKeyboardCompatibleBottomSheet
            />
        </YStack>
    );
};
